import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfDay,
  format,
  getDay,
  isAfter,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { MapBackArrowLeftIcon, MapBackArrowRightIcon, MapUpdateTimeIcon } from '~/assets/icons';
import styles from './BaseCalendarPicker.module.scss';

type FocusedInput = 'start' | 'end';
type CalendarMode = 'single' | 'range';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
}

interface BaseCalendarPickerLabels {
  buttonLabel: string;
  timeLabel: string;
  clickOrDragToSelect: string;
  /** When set, used for the inline typeable field title; falls back to clickOrDragToSelect. */
  dateInputHint: string;
  /** Shown on blur when typed date is not a valid calendar day or is in the future. */
  invalidDateFormat: string;
  startDatePlaceholder: string;
  endDatePlaceholder: string;
  previousMonth: string;
  nextMonth: string;
  todayNote: string;
  invalidDateRange: string;
  endBeforeStart: string;
  futureDateNotAllowed: string;
  weekdays: [string, string, string, string, string, string, string];
}

interface BaseCalendarPickerProps {
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  isLoading?: boolean;
  labels?: Partial<BaseCalendarPickerLabels>;
  monthYearFormatter?: (monthDate: Date) => string;
  triggerMode?: 'button' | 'inline';

  mode?: CalendarMode;
  dateRange?: DateRange;
  selectedDate?: Date | null;
  onDateChange?: (date: Date | null) => void;
  /**
   * Relevant only for `mode: 'single'`. When also `inline`, replaces the trigger with a typeable
   * dd/mm/yyyy field. Ignored for `range` mode.
   */
  allowInlineDateTyping?: boolean;
}

const DEFAULT_LABELS: BaseCalendarPickerLabels = {
  buttonLabel: 'Select date',
  timeLabel: 'Time',
  clickOrDragToSelect: 'Click to select date',
  dateInputHint: '',
  invalidDateFormat: 'Please enter a valid date in dd/mm/yyyy format',
  startDatePlaceholder: 'Start date',
  endDatePlaceholder: 'End date',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  todayNote: 'Today',
  invalidDateRange: 'Start date must be less than or equal to end date',
  endBeforeStart: 'End date cannot be earlier than start date',
  futureDateNotAllowed: 'Future date is not allowed',
  weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};

const INLINE_DATE_DELETE_INPUT_TYPES = new Set(['deleteContentBackward', 'deleteContentForward', 'deleteByCut', 'deleteByDrag']);

/**
 * Masks 8 max digits to `dd/`, `dd/mm/`, `dd/mm/yyyy`.
 * After 2 day digits and after 2 month digits a `/` is inserted automatically.
 * When `isDeletion` (user removed characters), we omit the trailing `/` for that
 * event so the slash can be deleted and corrected without reappearing.
 */
const formatDigitsToDdMmYyyy = (raw: string, isDeletion: boolean): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length === 0) return '';
  if (digits.length === 1) return digits;
  if (digits.length === 2) {
    if (isDeletion) return digits;
    return `${digits}/`;
  }
  if (digits.length === 3) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  if (digits.length === 4) {
    if (isDeletion) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2)}/`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/** In-progress (1–7 digits) validation for the single date field. */
const getPartialSingleDateErrorMessage = (onlyDigits: string, ui: BaseCalendarPickerLabels): string | null => {
  if (onlyDigits.length < 2) return null;
  const dd = Number.parseInt(onlyDigits.slice(0, 2), 10);
  if (Number.isNaN(dd) || dd < 1 || dd > 31) {
    return ui.invalidDateFormat;
  }
  if (onlyDigits.length < 4) return null;
  const mm = Number.parseInt(onlyDigits.slice(2, 4), 10);
  if (Number.isNaN(mm) || mm < 1) {
    return ui.invalidDateFormat;
  }
  if (mm > 12) {
    return ui.invalidDateFormat;
  }
  return null;
};

const evaluateEightDigitSingleDate = (
  onlyDigits: string,
  today: Date,
  ui: BaseCalendarPickerLabels
):
  | { ok: true; date: Date }
  | { ok: false; message: string } => {
  if (onlyDigits.length !== 8) {
    return { ok: false, message: ui.invalidDateFormat };
  }
  const parsed = tryParseStrictDdMmYyyy(onlyDigits);
  if (!parsed) {
    return { ok: false, message: ui.invalidDateFormat };
  }
  if (isAfter(parsed, today)) {
    return { ok: false, message: ui.invalidDateFormat };
  }
  return { ok: true, date: parsed };
};

const tryParseStrictDdMmYyyy = (eightDigits: string): Date | null => {
  if (eightDigits.length !== 8) return null;
  const dd = Number.parseInt(eightDigits.slice(0, 2), 10);
  const mm = Number.parseInt(eightDigits.slice(2, 4), 10);
  const yyyy = Number.parseInt(eightDigits.slice(4, 8), 10);
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return null;
  const d = new Date(yyyy, mm - 1, dd);
  if (!isValid(d) || d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) {
    return null;
  }
  return startOfDay(d);
};

const getMonthGridStart = (month: Date): Date => {
  const monthStart = startOfMonth(month);
  const dayIndex = getDay(monthStart);
  const mondayBasedOffset = dayIndex === 0 ? 6 : dayIndex - 1;
  return addDays(monthStart, -mondayBasedOffset);
};

const getCalendarDays = (month: Date): CalendarDay[] => {
  const monthStart = startOfMonth(month);
  const gridStart = getMonthGridStart(month);
  const gridEnd = addDays(gridStart, 41);

  return eachDayOfInterval({ start: gridStart, end: gridEnd }).map((date) => ({
    date,
    isCurrentMonth: isSameMonth(date, monthStart),
    isToday: isToday(date),
  }));
};

const BaseCalendarPicker: React.FC<BaseCalendarPickerProps> = ({
  onDateRangeChange,
  isLoading = false,
  labels,
  monthYearFormatter,
  triggerMode = 'button',
  mode = 'range',
  dateRange: externalDateRange,
  selectedDate: externalSelectedDate,
  onDateChange,
  allowInlineDateTyping = false,
}) => {
  const allowInlineDateTypingEnabled = allowInlineDateTyping && mode === 'single';
  const ui = useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inlineTypeInputRef = useRef<HTMLInputElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [inlineTypeDraft, setInlineTypeDraft] = useState('');
  const [isInlineTypeFocused, setIsInlineTypeFocused] = useState(false);

  const dateRange = externalDateRange || { start: null, end: null };
  const selectedDate = externalSelectedDate || null;

  const externalRangeStartKey = dateRange.start != null ? dateRange.start.getTime() : null;
  const externalRangeEndKey = dateRange.end != null ? dateRange.end.getTime() : null;
  const externalSelectedKey = selectedDate != null ? selectedDate.getTime() : null;

  const [tempStart, setTempStart] = useState<Date | null>(dateRange.start ? startOfDay(dateRange.start) : null);
  const [tempEnd, setTempEnd] = useState<Date | null>(dateRange.end ? startOfDay(dateRange.end) : null);
  const [tempSingleDate, setTempSingleDate] = useState<Date | null>(selectedDate ? startOfDay(selectedDate) : null);

  const [focusedInput, setFocusedInput] = useState<FocusedInput>('start');
  const [invalidFocusedInput, setInvalidFocusedInput] = useState<FocusedInput | null>(null);
  const [hoverMessage, setHoverMessage] = useState('');
  const [blinkDayKey, setBlinkDayKey] = useState<string | null>(null);

  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<Date>(() => {
    if (mode === 'single') {
      return selectedDate ? startOfDay(selectedDate) : new Date();
    } else {
      if (dateRange.start) return startOfDay(dateRange.start);
      if (dateRange.end) return startOfDay(dateRange.end);
      return new Date();
    }
  });

  const blinkTimeoutRef = useRef<number | null>(null);

  const clearBlinkTimer = useCallback(() => {
    if (blinkTimeoutRef.current) {
      window.clearTimeout(blinkTimeoutRef.current);
      blinkTimeoutRef.current = null;
    }
  }, []);

  const triggerDayBlink = useCallback(
    (date: Date) => {
      const key = format(date, 'yyyy-MM-dd');
      clearBlinkTimer();
      setBlinkDayKey(key);
      blinkTimeoutRef.current = window.setTimeout(() => {
        setBlinkDayKey(null);
        blinkTimeoutRef.current = null;
      }, 900);
    },
    [clearBlinkTimer]
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setInvalidFocusedInput(null);
    setHoverMessage('');
  }, []);

  useEffect(() => {
    if (mode === 'single') {
      setTempSingleDate(selectedDate ? startOfDay(selectedDate) : null);
      return;
    }
    setTempStart(dateRange.start ? startOfDay(dateRange.start) : null);
    setTempEnd(dateRange.end ? startOfDay(dateRange.end) : null);
  }, [mode, externalRangeStartKey, externalRangeEndKey, externalSelectedKey]);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'single') {
      setTempSingleDate(selectedDate ? startOfDay(selectedDate) : null);
      if (selectedDate) {
        triggerDayBlink(startOfDay(selectedDate));
      }
    } else {
      setTempStart(dateRange.start ? startOfDay(dateRange.start) : null);
      setTempEnd(dateRange.end ? startOfDay(dateRange.end) : null);

      if (dateRange.start && !dateRange.end) {
        setFocusedInput('end');
      } else if (!dateRange.start && dateRange.end) {
        setFocusedInput('start');
      }

      if (dateRange.start) {
        triggerDayBlink(startOfDay(dateRange.start));
      }
    }

    setInvalidFocusedInput(null);
    setHoverMessage('');

    const focusDate =
      mode === 'single' ? (selectedDate ?? new Date()) : (dateRange.start ?? dateRange.end ?? new Date());
    setCurrentCalendarMonth(startOfDay(focusDate));
  }, [dateRange.end, dateRange.start, isOpen, mode, selectedDate, triggerDayBlink]);

  useEffect(() => {
    return () => clearBlinkTimer();
  }, [clearBlinkTimer]);

  useEffect(() => {
    if (!allowInlineDateTypingEnabled || triggerMode !== 'inline') return;
    if (isInlineTypeFocused) return;
    if (selectedDate) {
      setInlineTypeDraft(format(startOfDay(selectedDate), 'dd/MM/yyyy'));
    } else {
      setInlineTypeDraft('');
    }
  }, [allowInlineDateTypingEnabled, triggerMode, isInlineTypeFocused, externalSelectedKey, selectedDate]);

  useEffect(() => {
    if (!hoverMessage) return undefined;
    const timer = window.setTimeout(() => setHoverMessage(''), 2200);
    return () => window.clearTimeout(timer);
  }, [hoverMessage]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeDropdown, isOpen]);

  const calendarDays = useMemo(() => getCalendarDays(currentCalendarMonth), [currentCalendarMonth]);
  const today = startOfDay(new Date());

  // Normalize dates based on mode
  const normalizedStartDate = useMemo(() => {
    if (mode === 'single') return null;
    return tempStart ? startOfDay(tempStart) : null;
  }, [tempStart, mode]);

  const normalizedEndDate = useMemo(() => {
    if (mode === 'single') return null;
    return tempEnd ? startOfDay(tempEnd) : null;
  }, [tempEnd, mode]);

  const normalizedSingleDate = useMemo(() => {
    if (mode !== 'single') return null;
    return tempSingleDate ? startOfDay(tempSingleDate) : null;
  }, [tempSingleDate, mode]);

  const monthYearLabel = useMemo(() => {
    if (monthYearFormatter) return monthYearFormatter(currentCalendarMonth);
    return format(currentCalendarMonth, 'MM/yyyy');
  }, [currentCalendarMonth, monthYearFormatter]);

  const handleDaySelect = useCallback(
    (day: Date) => {
      // Reject future dates
      if (isAfter(day, today)) {
        setHoverMessage(ui.futureDateNotAllowed);
        return;
      }

      setHoverMessage('');

      if (mode === 'single') {
        const nextDate = startOfDay(day);
        setTempSingleDate(nextDate);
        if (onDateChange) {
          onDateChange(nextDate);
        }
        if (allowInlineDateTypingEnabled && triggerMode === 'inline') {
          setInlineTypeDraft(format(nextDate, 'dd/MM/yyyy'));
        }
        setCurrentCalendarMonth(startOfMonth(day));
        triggerDayBlink(day);
        closeDropdown();
      } else {
        // Range mode logic (unchanged)
        if (focusedInput === 'start') {
          if (tempEnd && day.getTime() > startOfDay(tempEnd).getTime()) {
            setInvalidFocusedInput('start');
            setHoverMessage(ui.invalidDateRange);
            return;
          }

          const nextStart = startOfDay(day);
          setTempStart(nextStart);
          setInvalidFocusedInput(null);

          onDateRangeChange(nextStart, tempEnd);
        } else {
          if (tempStart && day.getTime() < startOfDay(tempStart).getTime()) {
            setInvalidFocusedInput('end');
            setHoverMessage(ui.endBeforeStart);
            return;
          }

          const nextEnd = endOfDay(day);
          setTempEnd(nextEnd);
          setInvalidFocusedInput(null);

          onDateRangeChange(tempStart, nextEnd);
        }

        setCurrentCalendarMonth(startOfMonth(day));
        triggerDayBlink(day);
      }
    },
    [
      focusedInput,
      onDateRangeChange,
      onDateChange,
      tempEnd,
      tempStart,
      today,
      triggerDayBlink,
      ui.endBeforeStart,
      ui.futureDateNotAllowed,
      ui.invalidDateRange,
      mode,
      closeDropdown,
      allowInlineDateTypingEnabled,
      triggerMode,
    ]
  );

  const handleCalendarKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isOpen) return;

      let focusedDate: Date | null = null;
      if (mode === 'single') {
        focusedDate = normalizedSingleDate ?? currentCalendarMonth;
      } else {
        focusedDate = focusedInput === 'start' ? normalizedStartDate : normalizedEndDate;
        focusedDate = focusedDate ?? currentCalendarMonth;
      }

      const baseDate = focusedDate;
      let nextDate: Date | null = null;

      if (event.key === 'ArrowLeft') nextDate = addDays(baseDate, -1);
      if (event.key === 'ArrowRight') nextDate = addDays(baseDate, 1);
      if (event.key === 'ArrowUp') nextDate = addDays(baseDate, -7);
      if (event.key === 'ArrowDown') nextDate = addDays(baseDate, 7);

      if (nextDate) {
        event.preventDefault();
        setCurrentCalendarMonth(startOfMonth(nextDate));
        handleDaySelect(nextDate);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        closeDropdown();
      }
    },
    [
      closeDropdown,
      currentCalendarMonth,
      focusedInput,
      handleDaySelect,
      isOpen,
      mode,
      normalizedEndDate,
      normalizedSingleDate,
      normalizedStartDate,
    ]
  );

  const renderDateInputButton = useCallback(
    (input: FocusedInput, shouldGrow = true) => {
      const isStartInput = input === 'start';
      const selectedDateValue = isStartInput ? normalizedStartDate : normalizedEndDate;
      const isFocused = isOpen && focusedInput === input;
      const isInvalid = invalidFocusedInput === input;
      const placeholder = isStartInput ? ui.startDatePlaceholder : ui.endDatePlaceholder;
      const displayText = selectedDateValue ? format(selectedDateValue, 'dd/MM/yyyy') : placeholder;
      const textClassName = selectedDateValue ? 'text-gray-700' : 'text-gray-400';
      const sizingClassName = shouldGrow ? 'flex-1' : styles.inlineTriggerDateInput;
      const buttonClassName = `flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${styles.headerDateInput} ${sizingClassName} ${
        isFocused ? styles.headerDateInputActive : ''
      } ${isInvalid ? 'border-red-500 !border-red-500' : ''}`;

      return (
        <button
          type='button'
          onClick={() => {
            setFocusedInput(input);
            setInvalidFocusedInput(null);
            setIsOpen(true);

            if (selectedDateValue) {
              setCurrentCalendarMonth(startOfMonth(selectedDateValue));
              triggerDayBlink(selectedDateValue);
            }
          }}
          onMouseEnter={() => setInvalidFocusedInput(null)}
          disabled={isLoading}
          className={buttonClassName}
          title={ui.clickOrDragToSelect}
        >
          <span className={`truncate ${textClassName}`}>{displayText}</span>
          <MapUpdateTimeIcon className='ml-auto h-4 w-4 text-gray-500' />
        </button>
      );
    },
    [focusedInput, invalidFocusedInput, isLoading, isOpen, normalizedEndDate, normalizedStartDate, triggerDayBlink, ui]
  );

  const renderSingleDateInputButton = useCallback(() => {
    const selectedDateValue = normalizedSingleDate;
    const isFocused = isOpen;
    const placeholder = ui.startDatePlaceholder;
    const displayText = selectedDateValue ? format(selectedDateValue, 'dd/MM/yyyy') : placeholder;
    const textClassName = selectedDateValue ? 'text-gray-700' : 'text-gray-400';
    const buttonClassName = `flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${styles.headerDateInput} flex-1 ${
      isFocused ? styles.headerDateInputActive : ''
    }`;

    return (
      <button
        type='button'
        onClick={() => {
          setInvalidFocusedInput(null);
          setIsOpen(true);

          if (selectedDateValue) {
            setCurrentCalendarMonth(startOfMonth(selectedDateValue));
            triggerDayBlink(selectedDateValue);
          }
        }}
        onMouseEnter={() => setInvalidFocusedInput(null)}
        disabled={isLoading}
        className={buttonClassName}
        title={ui.clickOrDragToSelect}
      >
        <span className={`truncate ${textClassName}`}>{displayText}</span>
        <MapUpdateTimeIcon className='ml-auto h-4 w-4 text-gray-500' />
      </button>
    );
  }, [isOpen, normalizedSingleDate, ui, isLoading, triggerDayBlink]);

  const inputHintTitle = ui.dateInputHint || ui.clickOrDragToSelect;

  const applyTypedEightDigits = useCallback(
    (eight: string) => {
      const result = evaluateEightDigitSingleDate(eight, today, ui);
      if (!result.ok) {
        setHoverMessage(result.message);
        return false;
      }
      setHoverMessage('');
      setInvalidFocusedInput(null);
      setTempSingleDate(result.date);
      onDateChange?.(result.date);
      setCurrentCalendarMonth(startOfMonth(result.date));
      triggerDayBlink(result.date);
      return true;
    },
    [onDateChange, today, triggerDayBlink, ui]
  );

  const handleInlineTypeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputType = (e.nativeEvent as InputEvent).inputType ?? '';
    const isDeletion = INLINE_DATE_DELETE_INPUT_TYPES.has(inputType);
    const next = formatDigitsToDdMmYyyy(e.target.value, isDeletion);
    setInlineTypeDraft(next);
    const onlyDigits = next.replace(/\D/g, '');
    if (onlyDigits.length < 8) {
      const partErr = getPartialSingleDateErrorMessage(onlyDigits, ui);
      setHoverMessage(partErr ?? '');
      return;
    }
    applyTypedEightDigits(onlyDigits);
  };

  const handleInlineTypeFocus = () => {
    setIsInlineTypeFocused(true);
    setInvalidFocusedInput(null);
    setIsOpen(true);
    if (normalizedSingleDate) {
      setCurrentCalendarMonth(startOfMonth(normalizedSingleDate));
      triggerDayBlink(normalizedSingleDate);
    }
  };

  const handleInlineTypeBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    setIsInlineTypeFocused(false);
    const onlyDigits = e.currentTarget.value.replace(/\D/g, '');

    if (onlyDigits.length === 0) {
      if (onDateChange) onDateChange(null);
      setTempSingleDate(null);
      setInlineTypeDraft('');
      setHoverMessage('');
      return;
    }

    if (onlyDigits.length < 8) {
      if (selectedDate) {
        setInlineTypeDraft(format(startOfDay(selectedDate), 'dd/MM/yyyy'));
      } else {
        setInlineTypeDraft('');
      }
      const partErr = getPartialSingleDateErrorMessage(onlyDigits, ui);
      setHoverMessage(partErr || ui.invalidDateFormat);
      return;
    }

    if (!applyTypedEightDigits(onlyDigits)) {
      if (selectedDate) {
        setInlineTypeDraft(format(startOfDay(selectedDate), 'dd/MM/yyyy'));
      } else {
        setInlineTypeDraft('');
      }
    }
  };

  const handleInlineTypeKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (selectedDate) {
        setInlineTypeDraft(format(startOfDay(selectedDate), 'dd/MM/yyyy'));
      } else {
        setInlineTypeDraft('');
      }
      setHoverMessage('');
      e.currentTarget.blur();
    }
  };

  const renderSingleDateInlineTypeable = () => {
    const isFocused = isOpen || isInlineTypeFocused;
    const shownClass = normalizedSingleDate || inlineTypeDraft ? 'text-gray-700' : 'text-gray-400';
    const fieldClass = `flex w-full min-w-0 items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${styles.headerDateInput} flex-1 ${
      isFocused ? styles.headerDateInputActive : ''
    }`;
    return (
      <div className={fieldClass}>
        <input
          ref={inlineTypeInputRef}
          type='text'
          inputMode='numeric'
          maxLength={10}
          autoComplete='off'
          name='inline-birth-date'
          spellCheck={false}
          disabled={isLoading}
          className={`${styles.inlineDateTypeInput} ${shownClass}`}
          placeholder={ui.startDatePlaceholder}
          title={inputHintTitle}
          aria-label={inputHintTitle}
          value={inlineTypeDraft}
          onChange={handleInlineTypeChange}
          onFocus={handleInlineTypeFocus}
          onBlur={handleInlineTypeBlur}
          onKeyDown={handleInlineTypeKeyDown}
        />
        <button
          type='button'
          tabIndex={-1}
          disabled={isLoading}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => inlineTypeInputRef.current?.focus()}
          className='ml-auto flex shrink-0 p-0'
          aria-label={inputHintTitle}
        >
          <MapUpdateTimeIcon className='h-4 w-4 text-gray-500' />
        </button>
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className={`relative ${triggerMode === 'inline' ? styles.inlineCalendarPicker : ''}`}>
      {/* Trigger Button */}
      {triggerMode === 'button' && mode === 'range' ? (
        <button
          type='button'
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={isLoading}
          className={styles.selectDateButton}
        >
          {ui.buttonLabel}
        </button>
      ) : triggerMode === 'inline' && mode === 'range' ? (
        <div className={styles.inlineTriggerRow}>
          {renderDateInputButton('start', true)}
          <span className={styles.calendarGap}>-</span>
          {renderDateInputButton('end', true)}
        </div>
      ) : triggerMode === 'button' && mode === 'single' ? (
        <button
          type='button'
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={isLoading}
          className={styles.selectDateButton}
        >
          {ui.buttonLabel}
        </button>
      ) : triggerMode === 'inline' && mode === 'single' && allowInlineDateTypingEnabled ? (
        <div className={styles.inlineTriggerRow}>{renderSingleDateInlineTypeable()}</div>
      ) : (
        <div className={styles.inlineTriggerRow}>{renderSingleDateInputButton()}</div>
      )}

      {isOpen && (
        <div
          className={`absolute right-0 top-[calc(100%+8px)] z-50 border border-gray-200 bg-white shadow-lg ${styles.headerPopover} ${
            triggerMode === 'inline' ? styles.headerPopoverInline : ''
          }`}
          onKeyDown={handleCalendarKeyDown}
          tabIndex={0}
        >
          <p className={styles.headerTimeLabel}>{ui.timeLabel}</p>

          {/* Render date inputs inside popover only for button mode */}
          {triggerMode === 'button' && mode === 'range' && (
            <div className={`flex items-center ${styles.headerInputsRow}`}>
              {renderDateInputButton('start')}
              <span className={styles.calendarGap}>-</span>
              {renderDateInputButton('end')}
            </div>
          )}

          {triggerMode === 'button' && mode === 'single' && (
            <div className={`flex items-center ${styles.headerInputsRow}`}>{renderSingleDateInputButton()}</div>
          )}

          <div className={styles.calendarSectionWrapper}>
            <div className='flex items-center justify-between'>
              <p className='text-sm font-medium text-gray-900'>{monthYearLabel}</p>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={() => setCurrentCalendarMonth((prev) => subMonths(prev, 1))}
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-sm text-gray-600 transition hover:bg-gray-100 ${styles.calendarNavButton}`}
                  aria-label={ui.previousMonth}
                >
                  <MapBackArrowLeftIcon className='h-4 w-4' />
                </button>
                <button
                  type='button'
                  onClick={() => setCurrentCalendarMonth((prev) => addMonths(prev, 1))}
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-sm text-gray-600 transition hover:bg-gray-100 ${styles.calendarNavButton}`}
                  aria-label={ui.nextMonth}
                >
                  <MapBackArrowRightIcon className='h-4 w-4' />
                </button>
              </div>
            </div>

            <div className='grid grid-cols-7 gap-1'>
              {ui.weekdays.map((dayLabel, index) => (
                <span key={`${dayLabel}-${index}`} className='text-center text-xs font-medium text-gray-500'>
                  {dayLabel}
                </span>
              ))}
            </div>

            <div className='grid grid-cols-7 gap-1' onMouseLeave={() => setHoverMessage('')}>
              {calendarDays.map((day) => {
                const isFuture = isAfter(day.date, today);

                // Single mode logic
                if (mode === 'single') {
                  const isSelected = normalizedSingleDate ? isSameDay(day.date, normalizedSingleDate) : false;
                  const dayClassName = [styles.calendarDay];
                  const dayTooltip = day.isToday
                    ? ui.todayNote
                    : isSelected
                      ? ui.clickOrDragToSelect
                      : isFuture
                        ? ui.futureDateNotAllowed
                        : undefined;
                  const dayKey = format(day.date, 'yyyy-MM-dd');

                  if (!day.isCurrentMonth) dayClassName.push(styles.calendarDayDisabled);
                  if (isFuture) dayClassName.push(styles.calendarDayFuture);
                  if (day.isToday) dayClassName.push(styles.calendarDayToday);
                  if (isSelected && !isFuture) dayClassName.push(styles.calendarDaySelected);
                  if (blinkDayKey === dayKey) dayClassName.push(styles.calendarDayBlink);

                  return (
                    <button
                      key={day.date.toISOString()}
                      type='button'
                      onClick={() => handleDaySelect(day.date)}
                      onMouseEnter={() => {
                        setInvalidFocusedInput(null);

                        if (day.isToday) {
                          setHoverMessage(ui.todayNote);
                          return;
                        }

                        if (isFuture) {
                          setHoverMessage(ui.futureDateNotAllowed);
                          return;
                        }

                        setHoverMessage('');
                      }}
                      onMouseLeave={() => setHoverMessage('')}
                      className={dayClassName.join(' ')}
                      title={dayTooltip}
                    >
                      {format(day.date, 'd')}
                    </button>
                  );
                }

                // Range mode logic (unchanged)
                const isStartSelected = normalizedStartDate ? isSameDay(day.date, normalizedStartDate) : false;
                const isEndSelected = normalizedEndDate ? isSameDay(day.date, normalizedEndDate) : false;
                const isBothSameDay = isStartSelected && isEndSelected;

                const isBetweenDates =
                  normalizedStartDate &&
                  normalizedEndDate &&
                  day.date.getTime() > normalizedStartDate.getTime() &&
                  day.date.getTime() < normalizedEndDate.getTime();

                // CSS styling logic:
                let isFocusedSelectedDay = false;
                let isSecondarySelectedDay = false;

                if (isBothSameDay) {
                  isFocusedSelectedDay = true;
                  isSecondarySelectedDay = false;
                } else if (normalizedStartDate && normalizedEndDate) {
                  if (isStartSelected || isEndSelected) {
                    isFocusedSelectedDay = true;
                    isSecondarySelectedDay = false;
                  } else if (isBetweenDates) {
                    isFocusedSelectedDay = false;
                    isSecondarySelectedDay = true;
                  }
                } else {
                  isFocusedSelectedDay = focusedInput === 'start' ? isStartSelected : isEndSelected;
                  isSecondarySelectedDay = focusedInput === 'start' ? isEndSelected : isStartSelected;
                }

                const dayClassName = [styles.calendarDay];
                const dayTooltip = day.isToday
                  ? ui.todayNote
                  : isStartSelected || isEndSelected
                    ? ui.clickOrDragToSelect
                    : isFuture
                      ? ui.futureDateNotAllowed
                      : undefined;
                const dayKey = format(day.date, 'yyyy-MM-dd');

                if (!day.isCurrentMonth) dayClassName.push(styles.calendarDayDisabled);
                if (isFuture) dayClassName.push(styles.calendarDayFuture);
                if (day.isToday) dayClassName.push(styles.calendarDayToday);
                if (isFocusedSelectedDay && !isFuture) dayClassName.push(styles.calendarDaySelected);
                if (isSecondarySelectedDay && !isFuture) dayClassName.push(styles.calendarDayInRange);
                if (blinkDayKey === dayKey) dayClassName.push(styles.calendarDayBlink);

                return (
                  <button
                    key={day.date.toISOString()}
                    type='button'
                    onClick={() => handleDaySelect(day.date)}
                    onMouseEnter={() => {
                      setInvalidFocusedInput(null);

                      if (day.isToday) {
                        setHoverMessage(ui.todayNote);
                        return;
                      }

                      if (isFuture) {
                        setHoverMessage(ui.futureDateNotAllowed);
                        return;
                      }

                      setHoverMessage('');
                    }}
                    onMouseLeave={() => setHoverMessage('')}
                    className={dayClassName.join(' ')}
                    title={dayTooltip}
                  >
                    {format(day.date, 'd')}
                  </button>
                );
              })}
            </div>

            {hoverMessage && <p className='text-center text-sm font-medium text-red-600'>{hoverMessage}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BaseCalendarPicker);
